import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type {
  ReactElement,
  CSSProperties,
  PropsWithChildren,
  HTMLAttributes,
  RefObject,
  ComponentPropsWithoutRef,
} from 'react';
import {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
  Fragment,
} from 'react';
import type {
  TableOptions,
  UseResizeColumnsOptions,
  UseGlobalFiltersOptions,
  UseSortByOptions,
  UseExpandedOptions,
  UsePaginationOptions,
  UseTableInstanceProps,
  UseGlobalFiltersInstanceProps,
  UseSortByInstanceProps,
  UseExpandedInstanceProps,
  UsePaginationInstanceProps,
  TableState,
  IdType,
  Renderer,
  Accessor,
  CellProps,
  ActionType,
  SortingRule,
  Column,
  Row,
} from 'react-table';
import {
  useTable,
  useFlexLayout,
  useResizeColumns,
  useExpanded,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from 'react-table';
import { Loader } from 'semantic-ui-react';
import { useIntersectionObserver, useWindowSize } from 'usehooks-ts';

import type { TablePaginationProps } from '@components/table/table-pagination-v7';
import TablePagination from '@components/table/table-pagination-v7';
import {
  TableScrollbarProxy,
  TableScrollScrims,
} from '@components/table/table-scroll-chrome';
import {
  defaultColumn,
  getWeightPerPixel,
  useColumnResize,
} from '@components/table/use-column-resize';
import { useDebouncedFilter } from '@components/table/use-debounced-filter';
import {
  getCanvasWidth,
  useHorizontalTableScroll,
} from '@components/table/use-horizontal-table-scroll';
import { useStickyTableHeader } from '@components/table/use-sticky-table-header';
import {
  useVirtualRows,
  type ScrollToIndexOptions,
} from '@components/virtual-list';
import { sortAlphanumeric } from '@utils/common';
import { getOffsetTopWithin, remToPx } from '@utils/dom-utils';
import { resetScrollToTop } from '@utils/scroll-reset';

import TableHead from './table-head';
import {
  ReactTableNoData,
  type ReactTableNoDataCustomComponent,
  type ReactTableNoDataProps,
} from './table-no-data';
import TableRow from './table-row';
import type {
  CustomColumnConfig,
  ReactTableCell,
  ReactTableColumnInstance,
  ReactTableExtendedColumnInterface,
  ReactTableHeaderGroup,
  ReactTableRow,
  ReactTableState,
} from './types';
import s from './react-table.scss';

export type {
  ReactTableCell,
  ReactTableHeaderGroup,
  ReactTableRow,
  ReactTableState,
};

type ReactTableOptions<T extends object> = TableOptions<T> &
  UseResizeColumnsOptions<T> &
  UseGlobalFiltersOptions<T> &
  UseSortByOptions<T> &
  UseExpandedOptions<T> &
  UsePaginationOptions<T> & {
    SubComponent?: (row: ReactTableRow<T>) => ReactElement;
  };

export type ReactTableExtendedState<T extends object> = ReactTableState<T> & {
  page: number;
  pages: number;
  sortedData: Array<
    Record<IdType<T>, any> & {
      _original: T;
    }
  >;
  data: Array<T>;
  filtered: any;
  filterStr: string;
  isStickyHeaderEnabled?: boolean;
  hasPaginationTop?: boolean;
};

export interface ReactTableInstance<T extends object>
  extends
    Omit<ReactTableOptions<T>, 'columns' | 'pageCount'>,
    UseTableInstanceProps<T>,
    UseGlobalFiltersInstanceProps<T>,
    UseSortByInstanceProps<T>,
    UseExpandedInstanceProps<T>,
    UsePaginationInstanceProps<T> {
  page: ReactTableRow<T>[];
  rows: ReactTableRow<T>[];
  rowsById: Record<string, ReactTableRow<T>>;
  headerGroups: Array<ReactTableHeaderGroup<T>>;
  footerGroups: Array<ReactTableHeaderGroup<T>>;
}

export type ReactTableExtendedInstance<T extends object> = {
  /**
   * Scrolls the row at the given index into view. A no-op unless the table is
   * virtualized.
   */
  scrollToIndex: (index: number, options?: ScrollToIndexOptions) => void;
  state: ReactTableExtendedState<T>;
};

type HeaderProps<D extends object> = ReactTableInstance<D> & {
  column: ReactTableColumnInstance<D>;
};

type FooterProps<D extends object> = ReactTableInstance<D> & {
  column: ReactTableColumnInstance<D>;
};

export type ReactTableOnSortedChange<T extends object> = (
  sortedBy: Array<{ id: string; desc?: boolean }>,
  tableInstance: ReactTableInstance<T>,
) => any;

export type ReactTableColumnProps<T extends object> = {
  id?: IdType<T>;
  accessor?: IdType<T> | Accessor<T>;
  Header?: Renderer<HeaderProps<T>>;
  Footer?: Renderer<FooterProps<T>>;
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  config?: CustomColumnConfig<T>;
  Cell?: Renderer<
    ReactTableRow<T> & { row: Record<IdType<T>, any>; value: any }
  >;
} & ReactTableExtendedColumnInterface;

export interface ReactTableProps<T extends object> extends Omit<
  ComponentPropsWithoutRef<'table'>,
  'children'
> {
  /* General options ------------------------------------------------------- */

  // The array of rows holding the data to display in the table—can be either
  // contains an array of items (because policy data is screwy) or objects
  data: Array<T> | null;

  // Array of column definitions for the data set
  columns: Array<ReactTableColumnProps<T>>;

  // ReactTable should be wrapped around a function to be used for the actual rendering
  children: (
    state: ReactTableExtendedState<T>,
    renderTable: () => ReactElement,
    tableOptions: ReactTableProps<T>,
  ) => ReactElement;

  // Callback to get the reference to the table instance
  tableRef?: (tableInstance: ReactTableExtendedInstance<T>) => void;

  // Is the table currently loading
  // If so, we display a loading message
  loading?: boolean;

  // Allows to override the default loading widget with a user-defined component
  LoadingComponent?: (conf: { loading: boolean }) => ReactElement;

  // Minimum number of rows to display
  // If there are fewer rows in the data set, the grid will be padded with empty rows
  minRows?: number;

  // Are the grid columns resizable? (True by default)
  // Each column also has an optional resizable option. By default, all columns are resizable if the table is resizable
  resizable?: boolean;

  // Component to render if there are no rows to display
  NoDataComponent?: ReactTableNoDataCustomComponent;

  // Provide properties to the NoDataComponent
  noDataProps?: Omit<
    ReactTableNoDataProps,
    'CustomComponent' | 'loading' | 'filterStr'
  >;

  // Provide additional properties to use at the TR row level
  getTrProps?: (
    state: ReactTableState<T>,
    row: ReactTableRow<T>,
  ) => HTMLAttributes<HTMLTableRowElement>;
  // Provide additional properties to use at the TD cell level
  getTdProps?: (
    state: ReactTableState<T>,
    cell: ReactTableCell<T>,
  ) => HTMLAttributes<HTMLTableCellElement>;

  // getExtraRowProps can be used to augment the row object with addition properties
  // This function is called before calling the column.Cell to provide user-defined
  // values to the row property
  getExtraRowProps?: <P extends object>() => P;

  // Component to use for displaying the column headers
  TableHeadComponent?: () => ReactElement;

  // Properties to pass in to the TheadComponent
  getTableHeadProps?: () => HTMLAttributes<HTMLTableSectionElement>;

  /* Row expansion options ------------------------------------------------- */

  // SubComponent to display when a row is expanded
  // It should be a function receiving the row object as parameter and returning a React element
  SubComponent?: (row: ReactTableRow<T>) => ReactElement;

  // Reducer function to react to changes to the expanded property
  // This function will be called any time the list of expanded rows changes, and
  // it should return the new expanded object (of the form { rowId: boolean })
  expandedReducer?: (previousExpanded: any, newExpanded: any) => any;

  columnWidths?: Record<string, number>;
  setColumnWidths?: (newWidths: Record<string, number>) => void;

  // Should we reset column widths when the columns array changes
  // Defaults to false
  resetColumnWidths?: boolean;

  // Hide the expander column that is normally added as a first column when there is a SubComponent
  hideExpanderColumn?: boolean;

  // By default, expanded rows collapse if the data changes. Set to false to override this behavior
  // NB: Set this to false when using in a React class component, because without memoizing the data array,
  // expanded rows will be collapsed on every component render
  collapseOnDataChange?: boolean;

  // Set to true to reset sorting to default when the data changes (default false).
  // NB: Set this to false when using in a React class component, because without memoizing the data array,
  // sorting will reset on every component render
  resetSortingOnDataChange?: boolean;

  /* Pagination options ---------------------------------------------------- */
  showPagination?: boolean;
  page?: number;
  pageSize?: number;
  getPaginationProps?: () => Partial<TablePaginationProps<T>>;
  onPageChange?: (newPageNumber: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  manualPagination?: boolean;
  pageCount?: number;

  /* Filtering options ----------------------------------------------------- */
  filterEnabled?: boolean;
  serverFilterEnabled?: boolean;

  // filtered is provided for backwards compatibility only
  // For new implementations, use filterStr
  filtered?: Array<{
    id: string;
    value: any;
  }>;

  // The filter string used to filter the table
  filterStr?: string;

  // Function for user-defined filtering mechanism
  filterMethod?: (conf: { value: any }, rowValues: any) => any;

  // Callback function invoked any time the filter value changes
  onFilteredChange?: (col: any, value: any) => any;

  /* Sorting options ------------------------------------------------------- */

  // Are the grid columns sortable? (True by default)
  // Each column also has an optional sortable option. By default, all columns are sortable if the table is sortable
  sortable?: boolean;

  // Callback function invoked any time the filter value changes
  onSortedChange?: ReactTableOnSortedChange<T>;

  // Default sorting to use for table
  defaultSorted?: Array<{
    id: string;
    desc?: boolean;
  }>;
  manualSorting?: boolean;
  multiSort?: boolean;

  // MinWidth setting to use for the table header
  // This property can be used to constrain the size of the table, e.g. for printing
  minHeaderWidth?: string;

  /**
   * Makes the table header sticky to prevent it scrolling out of the viewport
   * with the table body. This is optional as it can cause some tables to feel
   * more clunky depending on its placement and surrounding layout.
   *
   * The default behavior when set to `true` is to stick the table header to the
   * bottom of the TopNav. Alternatively, if a ref element is provided, it will
   * stick to the bottom of that element instead, which is useful for views that
   * may have existing sticky page headers etc.
   *
   * @example Stick to the top nav
   * ```tsx
   * <ReactTable stickyHeader />
   * ```
   *
   * @example Stick to a ref element
   * ```tsx
   * const { headerRef } = usePageContext();
   *
   * return (
   *   <ReactTable stickyHeader={headerRef} />
   * );
   * ```
   */
  stickyHeader?: boolean | RefObject<HTMLElement>;

  /**
   * Renders only the rows within the viewport (plus overscan) instead of the
   * full accumulated set, for long infinite-scroll lists. Window-scrolled only.
   */
  virtualized?: boolean | { estimateSize?: number; overscan?: number };

  // Optional styles to assign to table wrapper component
  style?: CSSProperties;

  autoFormatArrays?: boolean;

  noBorderWhenEmpty?: boolean;
}

interface CellContentProps<T extends object | Array<any>> {
  cellInfo: CellProps<T>;
  column: ReactTableColumnProps<T>;
  getExtraRowProps?: (row: ReactTableRow<T>) => any;
}

const CellContent = <T extends object | Array<any>>({
  cellInfo,
  column,
  getExtraRowProps = undefined,
}: CellContentProps<T>) => {
  let cellContents: string | ReactElement = cellInfo.value || '';
  if (column.Cell) {
    const row = {
      ...(cellInfo.row as ReactTableRow<T>),
      row: cellInfo.row.values,
      value: cellInfo.value,
    };
    const extendedRow = {
      ...row,
      ...(getExtraRowProps ? getExtraRowProps(row) : {}),
    };
    cellContents = (column.Cell as any)(extendedRow, cellInfo);
  }

  return <>{cellContents}</>;
};

const TableHeader = ({
  children,
  className,
  minWidth,
  style,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLElement> & { minWidth?: string }>) => (
  <div
    className={classNames('rt-thead', '-header', className)}
    style={{ ...style, minWidth }}
    {...rest}
  >
    {children}
  </div>
);

/**
 * @deprecated Superseded by `ClientDataTable` and `ServerDataTable` in
 * `@components/table`.
 */
const ReactTable = <T extends object | Array<any>>(
  props: ReactTableProps<T>,
) => {
  useStyles(s);

  const {
    LoadingComponent,
    NoDataComponent,
    noDataProps,
    SubComponent,
    TableHeadComponent = TableHeader,
    className = '',
    collapseOnDataChange = true,
    resetSortingOnDataChange = false,
    columns,
    data,
    defaultSorted,
    getExtraRowProps,
    getPaginationProps,
    getTdProps = () => ({}) as HTMLAttributes<HTMLTableCellElement>,
    getTableHeadProps,
    getTrProps = () => ({}) as HTMLAttributes<HTMLTableRowElement>,
    hideExpanderColumn,
    loading = false,
    manualPagination = false,
    manualSorting = false,
    minRows = 0,
    multiSort = true,
    noBorderWhenEmpty,
    onSortedChange,
    page: pageNumber = 0,
    pageCount: manualPageCount,
    pageSize,
    resizable: tableResizable = true,
    serverFilterEnabled = false,
    showPagination,
    sortable: tableSortable = true,
    columnWidths,
    setColumnWidths,
    resetColumnWidths = false,
    minHeaderWidth,
    stickyHeader,
    style,
    virtualized,
  } = props;

  const shouldSortTable = tableSortable ?? true;

  // Normalize the opt-in `virtualized` prop; a bare `true` adopts the defaults.
  const virtualizedOptions =
    virtualized === true ? {} : virtualized || undefined;
  const isVirtualized = Boolean(virtualizedOptions);

  const tableRef = useRef<HTMLDivElement | null>(null);
  const bodyCanvasRef = useRef<HTMLDivElement>(null);
  const bodyViewportRef = useRef<HTMLDivElement>(null);
  const headerCanvasRef = useRef<HTMLDivElement>(null);
  const proxyGhostRef = useRef<HTMLDivElement>(null);
  const proxyRef = useRef<HTMLDivElement>(null);
  const tbodyRef = useRef<HTMLDivElement>(null);
  const columnHeaderSentinelRef = useRef<HTMLDivElement>(null);

  // Sticky pagination + column-header height inside the table. Added to the
  // sticky page offset so scrollToIndex clears the whole sticky stack.
  const [virtualHeaderOffset, setVirtualHeaderOffset] = useState(0);

  const paginationProps = useMemo(
    () => getPaginationProps?.() || {},
    [getPaginationProps],
  );
  const filterEnabled = props.filterEnabled || !paginationProps.noFilter;
  const filterStr =
    props.filterStr || (props.filtered?.length && props.filtered[0]?.value);
  const filtered = [{ id: 'filter', value: filterStr }];
  const { filterMethod } = props;

  const expanderColumn = useMemo(() => {
    /* If the table is expandable then add the expander column */
    if (SubComponent && !hideExpanderColumn) {
      return {
        Header: '',
        id: 'expander',
        resizable: false,
        className: 'text-center',
        fixedWidth: 3,
        Cell: row => (
          // Applying the toggle expander props i.e onClick, style and title
          <div
            {...row.getToggleRowExpandedProps()}
            title="Click here to see more information"
            className="rt-td rt-expandable p-0"
            style={{ padding: '0' }}
          >
            <div
              className={classNames(
                'rt-expander',
                row.isExpanded ? '-open' : '',
              )}
            >
              •
            </div>
          </div>
        ),
      } as ReactTableColumnProps<T>;
    }
    return undefined;
  }, [SubComponent, hideExpanderColumn]);

  const sortType = useCallback(
    sortMethod =>
      (rowA: Row<T>, rowB: Row<T>, columnId: IdType<T>, desc?: boolean) =>
        sortMethod
          ? sortMethod(rowA.values[columnId], rowB.values[columnId], desc)
          : sortAlphanumeric(rowA.values[columnId], rowB.values[columnId]),
    [],
  );

  const columnsV7 = useMemo(() => {
    const cols = expanderColumn ? [expanderColumn, ...columns] : columns;
    return cols.map((col, index) => ({
      ...col,
      _index: index,
      Cell: (colInfo: CellProps<T>) => (
        <CellContent
          key={`tablecell_${col.id}`}
          column={col}
          cellInfo={colInfo}
          getExtraRowProps={getExtraRowProps}
        />
      ),
      sortType: sortType(col.sortMethod),
    }));
  }, [columns, expanderColumn, getExtraRowProps]);

  const hiddenColumns = useMemo(
    () =>
      columnsV7.filter(col => !(col.show ?? true)).map(col => String(col.id)),
    [columnsV7],
  );

  const visibleColumns = useMemo(
    () => columnsV7.filter(col => col.show ?? true),
    [columnsV7],
  );

  const fixedColumns = useMemo(
    () => columnsV7.filter(col => !!col.fixedWidth),
    [columnsV7],
  );

  const globalFilter = useCallback(
    (unfilteredRows, columnIds, filterValue) => {
      if (filterEnabled && filterMethod && !serverFilterEnabled) {
        const rowValues = unfilteredRows.map((row, index) => ({
          reactTableRowIndex: index,
          ...row.values,
        }));
        const filteredRowValues = filterMethod(
          { value: filterValue },
          rowValues,
        );
        return filteredRowValues
          .map(row => ({
            ...unfilteredRows[row.reactTableRowIndex],
            values: row,
          }))
          .sort((a, b) => a.index - b.index);
      }
      return unfilteredRows;
    },
    [filterEnabled, filterMethod, serverFilterEnabled],
  );

  const expandedReducer = useCallback(
    (previousExpanded, newExpanded) => {
      if (props.expandedReducer) {
        return props.expandedReducer(previousExpanded, newExpanded);
      }
      return newExpanded;
    },
    [props.expandedReducer],
  );

  const { calcWidthIncrement, getWidthsForColumns, initialColumnWidths } =
    useColumnResize({ columns: columnsV7, columnWidths, setColumnWidths });

  const stateReducer = useCallback(
    (
      state: TableState<T>,
      action: ActionType,
      previousState: TableState<T>,
    ) => {
      const newState = state as ReactTableState<T>;
      const prevState = previousState as ReactTableState<T>;
      switch (action.type) {
        case 'resetResize': {
          if (!resetColumnWidths) {
            return prevState;
          }
          break;
        }

        case 'toggleRowExpanded': {
          return {
            ...newState,
            expanded: expandedReducer(prevState.expanded, newState.expanded),
          };
        }

        case 'columnStartResizing': {
          return {
            ...newState,
            columnResizing: {
              ...newState.columnResizing,
              previousColumnWidths: newState.columnResizing.columnWidths,
            },
          };
        }

        case 'columnDoneResizing': {
          if (setColumnWidths) {
            setTimeout(
              () => setColumnWidths(newState.columnResizing.columnWidths),
              0,
            );
          }
          return {
            ...newState,
            columnResizing: {
              ...newState.columnResizing,
              previousColumnWidths: null,
            },
          };
        }

        case 'columnResizing': {
          const deltaX = action.clientX - (newState.columnResizing.startX ?? 0);
          const { isResizingColumn } = newState.columnResizing;
          const columnId =
            isResizingColumn || prevState.columnResizing.isResizingColumn;
          const origColumn = columnsV7.find(col => col.id === columnId);

          if (origColumn && deltaX !== 0) {
            const colsToRight = visibleColumns.filter(
              col =>
                (col.resizable ?? true) &&
                !col.fixedWidth &&
                // eslint-disable-next-line no-underscore-dangle
                col._index > origColumn._index,
            );
            const flexColumnsWidth = visibleColumns
              .filter(col => !col.fixedWidth)
              .reduce(
                (sum, col) =>
                  sum +
                  (newState.columnResizing.previousColumnWidths[col.id!] ||
                    initialColumnWidths[String(col.id)]),
                0,
              );
            const fixedColsWidth = fixedColumns.reduce(
              // col.fixedWidth is expressed in rem units, so we need to convert it to pixels
              (sum, col) => remToPx(col.fixedWidth!) + sum,
              0,
            );
            const weightPerPixel = getWeightPerPixel({
              canvasWidth: bodyCanvasRef.current?.scrollWidth || 0,
              fixedColumnsWidth: fixedColsWidth,
              flexColumnsWidth,
            });
            const colWidth = newState.columnResizing.columnWidth;
            const widthIncrement = calcWidthIncrement(
              origColumn,
              colWidth,
              Math.floor(deltaX * weightPerPixel),
            );

            const newColWidths = {
              ...prevState.columnResizing.columnWidths,
              ...getWidthsForColumns({
                columnToResize: origColumn,
                currentWidths: newState.columnResizing.previousColumnWidths,
                neighborColumns: colsToRight,
                newColumnWidth: colWidth + widthIncrement,
                widthDelta: widthIncrement,
              }),
            };

            return {
              ...newState,
              columnResizing: {
                ...newState.columnResizing,
                columnWidths: newColWidths,
              },
            };
          }
          return newState;
        }

        default: {
          return newState;
        }
      }
      return newState;
    },
    [columnsV7],
  );

  const useTableOptions: ReactTableOptions<T> = {
    columns: columnsV7 as ReadonlyArray<Column<T>>,
    data: data || [],
    defaultColumn,
    SubComponent,
    initialState: {
      globalFilter: filterStr,
      pageSize,
      pageIndex: pageNumber,
      sortBy: defaultSorted || [],
      hiddenColumns,
      columnResizing: {
        columnWidths: columnWidths || initialColumnWidths,
      },
    } as ReactTableState<T>,
    globalFilter,
    stateReducer,
    manualSortBy: manualSorting,
    ...(manualPagination
      ? {
          manualPagination,
          pageCount: manualPageCount,
        }
      : {}),
    disableMultiSort: !multiSort,
    disableSortRemove: true,
    disableSortBy: !shouldSortTable,
    autoResetExpanded: collapseOnDataChange ?? true,
    autoResetSortBy: resetSortingOnDataChange ?? false,
  };
  const tableInstance = useTable(
    useTableOptions,
    useFlexLayout,
    useResizeColumns,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
  ) as ReactTableInstance<T>;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    rowsById,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    setPageSize,
    state,
    setGlobalFilter,
    setHiddenColumns,
    setSortBy,
  } = tableInstance as ReactTableInstance<T>;
  const tableState = state as ReactTableState<T>;

  const unsortedRows = useMemo(
    () => Object.keys(rowsById).map(key => rowsById[key]),
    [rowsById],
  );

  useEffect(() => {
    if (hiddenColumns.join() !== state?.hiddenColumns?.join())
      setHiddenColumns(hiddenColumns);
  }, [hiddenColumns, setHiddenColumns]);

  useEffect(() => {
    if (manualPagination && pageNumber !== tableState.pageIndex)
      gotoPage(pageNumber);
  }, [manualPagination, pageNumber, gotoPage, tableState.pageIndex]);

  const [previousSortBy, setPreviousSortBy] = useState(tableState.sortBy);

  const onPageChange = useMemo(
    () => (value: number) => {
      if (tableState.pageIndex !== value) {
        gotoPage(value);
        if (props.onPageChange) props.onPageChange(value);
      }
    },
    [tableState.pageIndex, gotoPage, props.onPageChange],
  );

  const { isColumnHeaderPinned, stickyInsetTop } = useStickyTableHeader({
    sentinelRef: columnHeaderSentinelRef,
    stickyHeader,
    tableRef,
  });

  useEffect(() => {
    const sortCols = (sortDefs?: SortingRule<T>[]) =>
      sortDefs?.map(def => `${def.id}${def.desc ?? true}`)?.join();

    if (sortCols(tableState.sortBy) === sortCols(previousSortBy)) return;

    if (onSortedChange) onSortedChange(tableState.sortBy, tableInstance);
    setPreviousSortBy(tableState.sortBy);
    onPageChange(0);

    if (!stickyHeader || !tableRef.current) return;

    if (tableRef.current.getBoundingClientRect().top < stickyInsetTop) {
      resetScrollToTop(tableRef.current);
    }
  }, [
    previousSortBy,
    tableState.sortBy,
    onSortedChange,
    setPreviousSortBy,
    stickyHeader,
    stickyInsetTop,
  ]);

  const { currentFilter, onFilteredChange } = useDebouncedFilter({
    globalFilter: tableState.globalFilter,
    initialFilter: filterStr,
    onFilterChange: props.onFilteredChange,
    onPageChange,
    pageIndex: tableState.pageIndex,
    setGlobalFilter,
  });

  const onPageSizeChange = useMemo(
    () => (value: number) => {
      if (props.onPageSizeChange) props.onPageSizeChange(value);
      setPageSize(value);
    },
    [props.onPageSizeChange, setPageSize],
  );

  const visibleRows = showPagination
    ? page
    : shouldSortTable
      ? rows
      : unsortedRows;

  const {
    measureElement: measureVirtualRow,
    scrollMargin: virtualScrollMargin,
    scrollToIndex,
    totalSize: virtualTotalSize,
    virtualItems,
  } = useVirtualRows({
    count: visibleRows.length,
    enabled: isVirtualized,
    estimateSize: virtualizedOptions?.estimateSize,
    overscan: virtualizedOptions?.overscan,
    scrollMarginRef: tbodyRef,
    scrollPaddingStart: stickyInsetTop + virtualHeaderOffset,
  });

  const rowData = useMemo(
    () => rows.map(row => ({ ...row.values, _original: row.original })),
    [rows],
  );
  const extendedState = useMemo<ReactTableExtendedState<T>>(
    () => ({
      ...tableState,
      page: tableState.pageIndex,
      pages: pageCount,
      sortedData: rowData,
      data: data || [],
      filtered,
      filterStr,
      hasPaginationTop: showPagination,
      isStickyHeaderEnabled: !!stickyHeader,
    }),
    [
      tableState,
      pageCount,
      rowData,
      filterStr,
      filtered,
      data,
      showPagination,
      stickyHeader,
    ],
  );

  useEffect(() => {
    if (props.tableRef) {
      props.tableRef({
        ...tableInstance,
        scrollToIndex,
        state: extendedState,
      });
    }
  }, [props.tableRef, tableInstance, scrollToIndex]);

  const { height: windowHeight } = useWindowSize({ debounceDelay: 200 });

  // Re-measure the sticky-header offset on resize and first row mount; keyed on
  // row presence (not count) so page appends don't trigger a reflow read.
  const hasVisibleRows = visibleRows.length > 0;
  useEffect(() => {
    if (!isVirtualized) return;

    // Floored at zero: this is padding for the chrome above the body, and a
    // negative would push a scrolled-to row back under it.
    const offset = Math.max(
      0,
      getOffsetTopWithin(tbodyRef.current, tableRef.current),
    );

    setVirtualHeaderOffset(prev => (prev === offset ? prev : offset));
  }, [hasVisibleRows, isVirtualized, windowHeight]);

  // Available space between the sticky boundary and the bottom of the viewport.
  // `useWindowSize` is typed as `number` but its runtime returns `undefined`
  // on the server, which can leak through hydration. Coercing to 0 keeps
  // `intersectionRootMarginBottom` finite so the IntersectionObserver below
  // never gets a `NaNpx` rootMargin.
  const availableHeight = (windowHeight ?? 0) - stickyInsetTop;

  // Buffer of 100px makes the initial application of sticky state less jumpy by
  // firing an intersection event just before it reaches the actual boundary
  const intersectionRootMarginBottom = 100 - availableHeight;

  // Whether the current height of the table is (or is close to) overflowing the
  // available height
  const isProbablyOverflowing = tableRef.current
    ? tableRef.current.offsetHeight + 100 > availableHeight
    : false;

  const { ref, isIntersecting } = useIntersectionObserver({
    rootMargin: `0px 0px ${intersectionRootMarginBottom}px`,
    freezeOnceVisible: true,
  });

  // The leaf row: under grouped columns the rows above it carry summed widths and no
  // `fixedWidth`, which would count fixed columns in px as well as rem.
  const headerColumns = headerGroups[headerGroups.length - 1]?.headers ?? [];

  // Cells are shrink-0 over a definite basis, so the resolved widths are the canvas.
  const canvasWidth = getCanvasWidth(
    headerColumns.map(column => ({
      fixedWidth: column.fixedWidth,
      width: column.totalWidth,
    })),
  );

  useHorizontalTableScroll({
    bodyCanvasRef,
    bodyViewportRef,
    headerCanvasRef,
    measureKey: canvasWidth,
    proxyGhostRef,
    proxyRef,
    rootRef: tableRef,
  });

  const getFixedColumnStyle = (
    col: ReactTableHeaderGroup<T> | ReactTableColumnInstance<T>,
  ) =>
    col.fixedWidth
      ? {
          minWidth: `${col.fixedWidth}rem`,
          width: `${col.fixedWidth}rem`,
          maxWidth: `${col.fixedWidth}rem`,
          flex: `0 0 ${col.fixedWidth}rem`,
        }
      : {};

  const getColHeaderStyle = (col: ReactTableHeaderGroup<T>) => ({
    ...getFixedColumnStyle(col),
  });

  const getCellStyle = (cell: ReactTableCell<T>) => ({
    ...getFixedColumnStyle(cell.column),
  });

  const tableHeadProps = getTableHeadProps?.() || {};

  const table = (
    <div
      className={classNames(
        {
          ReactTable: true,
          [s.root]: true,
          [s.sticky]: stickyHeader && isProbablyOverflowing && isIntersecting,
          [s.noData]: !visibleRows.length && noBorderWhenEmpty,
        },
        className,
      )}
      ref={instance => {
        if (tableRef.current) return;

        tableRef.current = instance;
        ref(instance);
      }}
      style={style}
    >
      {showPagination ? (
        <div className={classNames('pagination-top', s.paginationTop)}>
          <TablePagination
            {...paginationProps}
            state={extendedState}
            page={tableState.pageIndex}
            pages={pageCount}
            pageSize={tableState.pageSize}
            rawData={rowData}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onFilteredChange={onFilteredChange}
            filterStr={currentFilter}
            noFilter={!filterEnabled}
          />
        </div>
      ) : null}
      <div
        {...getTableProps()}
        aria-label={props['aria-label']}
        className={classNames('rt-table', s.table)}
        // Replaces the `minWidth` `useFlexLayout` contributes, which would overflow
        // `.rt-table` past the root instead of panning the canvas.
        style={{ '--table-min-width': canvasWidth } as CSSProperties}
      >
        <div
          ref={columnHeaderSentinelRef}
          aria-hidden
          data-testid="rt-sticky-sentinel"
        />
        <div className={s.headerViewport} role="presentation">
          <div
            ref={headerCanvasRef}
            className={s.headerCanvas}
            role="presentation"
          >
            <TableHead
              Wrapper={TableHeadComponent}
              columns={columnsV7}
              getColumnStyle={getColHeaderStyle}
              headerGroups={headerGroups}
              isPinned={isColumnHeaderPinned}
              minWidth={minHeaderWidth}
              resizable={tableResizable}
              setSortBy={setSortBy}
              sortBy={extendedState.sortBy}
              tableHeadProps={tableHeadProps}
              visibleColumns={visibleColumns}
            />
          </div>
        </div>
        <div
          ref={bodyViewportRef}
          className={s.bodyViewport}
          role="presentation"
        >
          <div ref={bodyCanvasRef} className={s.bodyCanvas} role="presentation">
            {visibleRows.length ? (
              isVirtualized ? (
                <div
                  ref={tbodyRef}
                  {...getTableBodyProps()}
                  className={classNames('rt-tbody', s.virtualBody)}
                  style={{ height: virtualTotalSize }}
                >
                  {virtualItems.map(virtualItem => (
                    <div
                      key={virtualItem.key}
                      ref={measureVirtualRow}
                      className={classNames('rt-tr-group', s.virtualRow, {
                        [s.virtualRowLast]:
                          virtualItem.index === visibleRows.length - 1,
                      })}
                      data-index={virtualItem.index}
                      // Transparent in the a11y tree so the inner role="row" sits under the rowgroup.
                      role="presentation"
                      style={{ top: virtualItem.start - virtualScrollMargin }}
                    >
                      <TableRow
                        SubComponent={SubComponent}
                        getCellStyle={getCellStyle}
                        getTdProps={getTdProps}
                        getTrProps={getTrProps}
                        prepareRow={prepareRow}
                        row={visibleRows[virtualItem.index]}
                        rowIndex={virtualItem.index}
                        state={tableState}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rt-tbody">
                  {visibleRows.map((row, ind) => (
                    <Fragment key={row.id}>
                      <div {...getTableBodyProps()} className="rt-tr-group">
                        <TableRow
                          SubComponent={SubComponent}
                          getCellStyle={getCellStyle}
                          getTdProps={getTdProps}
                          getTrProps={getTrProps}
                          prepareRow={prepareRow}
                          row={row}
                          rowIndex={ind}
                          state={tableState}
                        />
                      </div>
                    </Fragment>
                  ))}
                  {[
                    ...Array(
                      Math.min(
                        Math.abs(tableState.pageSize - page.length || 0),
                        minRows,
                      ),
                    ).keys(),
                  ].map(index => (
                    <div
                      key={index}
                      {...getTableBodyProps()}
                      className="rt-tr-group"
                    >
                      <div
                        className={classNames(
                          'rt-tr',
                          (page.length + index) % 2 === 0 ? '-odd' : '-even',
                        )}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </div>
        </div>

        <TableScrollScrims />

        {visibleRows.length ? null : (
          <ReactTableNoData
            key="no-data-control"
            CustomComponent={NoDataComponent}
            loading={loading}
            // Use ReactTable's internal filter state when available to prevent NoData message
            // flash during debounce. We primarily use tableState.globalFilter (debounced) and
            // only fallback to the prop filterStr when the internal state is empty. This prevents
            // showing "No Data for X" when the parent passes stale filterStr but table isn't filtering.
            filterStr={tableState.globalFilter || filterStr}
            {...noDataProps}
            descriptors={noDataProps?.descriptors || {}}
          />
        )}
      </div>

      <TableScrollbarProxy ghostRef={proxyGhostRef} scrollerRef={proxyRef} />

      {/* Loading overlay */}
      <div className={classNames('-loading', loading ? '-active' : '')}>
        {LoadingComponent ? (
          LoadingComponent({ loading })
        ) : (
          <div className="-loading-inner">
            <Loader active={loading} size="huge" />
          </div>
        )}
      </div>
    </div>
  );
  const tableOptions = {
    ...props,
    onPageChange,
    onPageSizeChange,
    onFilteredChange,
  };
  return props.children(extendedState, () => table, tableOptions);
};

export default ReactTable;
